import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
    Card,
    Input,
    InputNumber,
    Select,
    DatePicker,
    Button,
    Space,
    Row,
    Col,
    Collapse,
    Typography,
    Tag,
    Tooltip
} from 'antd';
import {
    SearchOutlined,
    FilterOutlined,
    ClearOutlined,
    CalendarOutlined,
    ShopOutlined,
    DownOutlined,
    UpOutlined,
    BgColorsOutlined,
    ReloadOutlined,
    PrinterOutlined,
    ProductOutlined,
    TruckOutlined,
    UserOutlined,
    TagOutlined,
    CheckOutlined,
    CloseCircleOutlined,
    CloseOutlined,
    MessageOutlined,
    MenuOutlined,
    LineOutlined,
    NumberOutlined,
    LockOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { FILTER_TYPES, DEFAULT_FILTER_VALUES } from '../constants/filterTypes';
import { FilterManager } from '../utils/filterManager';
import SearchInput from './SearchInput';

const { Search, TextArea } = Input;
const { Option, OptGroup } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;
const { Panel } = Collapse;

const OrdersFilter = ({
    onFilterChange,
    onClearFilters,
    onApplyFilters,
    onReload,
    filters,
    loading,
    lockedEmployeeId = null
}) => {
    const [isFilterExpanded, setIsFilterExpanded] = useState(false);
    
    // Extract filter values for easier access with defaults
    // CRITICAL: Use !== undefined to allow null values (after clear filters)
    const {
        searchText = DEFAULT_FILTER_VALUES[FILTER_TYPES.SEARCH],
        selectedStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.STATUS],
    } = filters;
    
    // For selectedDateRange, explicitly check for undefined to preserve null after clear
    const selectedDateRange = filters.selectedDateRange !== undefined 
        ? filters.selectedDateRange 
        : DEFAULT_FILTER_VALUES[FILTER_TYPES.DATE_RANGE];
    
    const {
        selectedSellerAccount = DEFAULT_FILTER_VALUES[FILTER_TYPES.SELLER_ACCOUNT],
        selectedCustomizationStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.CUSTOMIZATION_STATUS],
        selectedPrinter = DEFAULT_FILTER_VALUES[FILTER_TYPES.PRINTER],
        selectedFulfillmentStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.FULFILLMENT_STATUS],
        selectedFulfillmentChannel = DEFAULT_FILTER_VALUES[FILTER_TYPES.FULFILLMENT_CHANNEL],
        selectedShipmentStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.SHIPMENT_STATUS],
        selectedShipmentDetail = DEFAULT_FILTER_VALUES[FILTER_TYPES.SHIPMENT_DETAIL],
        selectedSupporter = DEFAULT_FILTER_VALUES[FILTER_TYPES.SUPPORTER],
        selectedCategory = DEFAULT_FILTER_VALUES[FILTER_TYPES.CATEGORY],
        selectedMessageStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.MESSAGE_STATUS],
        selectedShipDateDue = DEFAULT_FILTER_VALUES[FILTER_TYPES.SHIP_DATE_DUE],
        selectedDeliveryDateDue = DEFAULT_FILTER_VALUES[FILTER_TYPES.DELIVERY_DATE_DUE],
        selectedConfirmShipment = DEFAULT_FILTER_VALUES[FILTER_TYPES.CONFIRM_SHIPMENT],
        selectedUpdatedLastMile = DEFAULT_FILTER_VALUES[FILTER_TYPES.UPDATED_LAST_MILE],
        selectedMockupSentAt = DEFAULT_FILTER_VALUES[FILTER_TYPES.MOCKUP_SENT_AT],
        selectedTitle = DEFAULT_FILTER_VALUES[FILTER_TYPES.TITLE],
        selectedSellerSku = DEFAULT_FILTER_VALUES[FILTER_TYPES.SELLER_SKU],
        selectedEmployeeId = DEFAULT_FILTER_VALUES[FILTER_TYPES.EMPLOYEE_ID],
        selectedExcludeTitle = DEFAULT_FILTER_VALUES[FILTER_TYPES.EXCLUDE_TITLE],
        selectedExcludeSellerSku = DEFAULT_FILTER_VALUES[FILTER_TYPES.EXCLUDE_SELLER_SKU],
        selectedBasecost = DEFAULT_FILTER_VALUES[FILTER_TYPES.BASECOST],
        selectedQuantity = DEFAULT_FILTER_VALUES[FILTER_TYPES.QUANTITY]
    } = filters;

    const [localFilters, setLocalFilters] = useState({
        searchText,
        selectedStatus: Array.isArray(selectedStatus) ? selectedStatus : (selectedStatus === 'all' ? [] : selectedStatus ? [selectedStatus] : []),
        selectedDateRange,
        selectedSellerAccount: Array.isArray(selectedSellerAccount) ? selectedSellerAccount : (selectedSellerAccount === 'all' ? [] : selectedSellerAccount ? [selectedSellerAccount] : []),
        selectedCustomizationStatus,
        selectedPrinter: Array.isArray(selectedPrinter) ? selectedPrinter : (selectedPrinter === 'all' ? [] : selectedPrinter ? [selectedPrinter] : []),
        selectedFulfillmentStatus,
        selectedFulfillmentChannel,
        selectedShipmentStatus: Array.isArray(selectedShipmentStatus) ? selectedShipmentStatus : (selectedShipmentStatus === 'all' ? [] : selectedShipmentStatus ? [selectedShipmentStatus] : []),
        selectedShipmentDetail,
        selectedSupporter,
        selectedCategory: Array.isArray(selectedCategory) ? selectedCategory : (selectedCategory === 'all' ? [] : selectedCategory ? [selectedCategory] : []),
        selectedMessageStatus,
        selectedShipDateDue,
        selectedDeliveryDateDue,
        selectedConfirmShipment,
        selectedUpdatedLastMile,
        selectedMockupSentAt,
        selectedTitle,
        selectedSellerSku,
        selectedEmployeeId,
        selectedExcludeTitle,
        selectedExcludeSellerSku,
        selectedBasecost,
        selectedQuantity
    });
    const { sellerAccounts, printers, users, categories } = useSelector(state => state.dataCommon);

    // Filter type mapping constant to avoid duplication
    const FILTER_TYPE_MAP = {
        'searchText': FILTER_TYPES.SEARCH,
        'selectedStatus': FILTER_TYPES.STATUS,
        'selectedDateRange': FILTER_TYPES.DATE_RANGE,
        'selectedSellerAccount': FILTER_TYPES.SELLER_ACCOUNT,
        'selectedCustomizationStatus': FILTER_TYPES.CUSTOMIZATION_STATUS,
        'selectedPrinter': FILTER_TYPES.PRINTER,
        'selectedFulfillmentStatus': FILTER_TYPES.FULFILLMENT_STATUS,
        'selectedFulfillmentChannel': FILTER_TYPES.FULFILLMENT_CHANNEL,
        'selectedShipmentStatus': FILTER_TYPES.SHIPMENT_STATUS,
        'selectedShipmentDetail': FILTER_TYPES.SHIPMENT_DETAIL,
        'selectedSupporter': FILTER_TYPES.SUPPORTER,
        'selectedCategory': FILTER_TYPES.CATEGORY,
        'selectedMessageStatus': FILTER_TYPES.MESSAGE_STATUS,
        'selectedShipDateDue': FILTER_TYPES.SHIP_DATE_DUE,
        'selectedDeliveryDateDue': FILTER_TYPES.DELIVERY_DATE_DUE,
        'selectedConfirmShipment': FILTER_TYPES.CONFIRM_SHIPMENT,
        'selectedUpdatedLastMile': FILTER_TYPES.UPDATED_LAST_MILE,
        'selectedMockupSentAt': FILTER_TYPES.MOCKUP_SENT_AT,
        'selectedTitle': FILTER_TYPES.TITLE,
        'selectedSellerSku': FILTER_TYPES.SELLER_SKU,
        'selectedEmployeeId': FILTER_TYPES.EMPLOYEE_ID,
        'selectedExcludeTitle': FILTER_TYPES.EXCLUDE_TITLE,
        'selectedExcludeSellerSku': FILTER_TYPES.EXCLUDE_SELLER_SKU,
        'selectedBasecost': FILTER_TYPES.BASECOST,
        'selectedQuantity': FILTER_TYPES.QUANTITY
    };

    // Sync local filters with props when they change externally (e.g., URL sync, clear filters)
    React.useEffect(() => {
        setLocalFilters({
            searchText,
            selectedStatus: Array.isArray(selectedStatus) ? selectedStatus : (selectedStatus === 'all' ? [] : selectedStatus ? [selectedStatus] : []),
            selectedDateRange,
            selectedSellerAccount: Array.isArray(selectedSellerAccount) ? selectedSellerAccount : (selectedSellerAccount === 'all' ? [] : selectedSellerAccount ? [selectedSellerAccount] : []),
            selectedCustomizationStatus,
            selectedPrinter: Array.isArray(selectedPrinter) ? selectedPrinter : (selectedPrinter === 'all' ? [] : selectedPrinter ? [selectedPrinter] : []),
            selectedFulfillmentStatus,
            selectedFulfillmentChannel,
            selectedShipmentStatus: Array.isArray(selectedShipmentStatus) ? selectedShipmentStatus : (selectedShipmentStatus === 'all' ? [] : selectedShipmentStatus ? [selectedShipmentStatus] : []),
            selectedShipmentDetail,
            selectedSupporter,
            selectedCategory: Array.isArray(selectedCategory) ? selectedCategory : (selectedCategory === 'all' ? [] : selectedCategory ? [selectedCategory] : []),
            selectedMessageStatus,
            selectedShipDateDue,
            selectedDeliveryDateDue,
            selectedConfirmShipment,
            selectedUpdatedLastMile,
            selectedMockupSentAt,
            selectedTitle,
            selectedSellerSku,
            selectedEmployeeId,
            selectedExcludeTitle,
            selectedExcludeSellerSku,
            selectedBasecost,
            selectedQuantity
        });
    }, [searchText, selectedStatus, selectedDateRange, selectedSellerAccount, selectedCustomizationStatus, selectedPrinter, selectedFulfillmentStatus, selectedFulfillmentChannel, selectedShipmentStatus, selectedShipmentDetail, selectedSupporter, selectedCategory, selectedMessageStatus, selectedShipDateDue, selectedDeliveryDateDue, selectedConfirmShipment, selectedUpdatedLastMile, selectedMockupSentAt, selectedTitle, selectedSellerSku, selectedEmployeeId, selectedExcludeTitle, selectedExcludeSellerSku, selectedBasecost, selectedQuantity]);

    // Memoize functions to prevent recreation on every render
    const handleDateRangeChange = useCallback((dates) => {
        setLocalFilters(prev => ({ ...prev, selectedDateRange: dates }));
    }, []);

    const handleClearFilters = useCallback(() => {
        // Clear Redux state first, then let useEffect sync to local state
        // This prevents the "flash" of old values when clearing
        onClearFilters();
    }, [onClearFilters]);

    // Handle individual filter changes (only update local state)
    const handleLocalFilterChange = useCallback((filterKey, value) => {
        setLocalFilters(prev => ({ ...prev, [filterKey]: value }));
    }, []);

    const handleShipmentDetailChange = useCallback((value) => {
        setLocalFilters(prev => ({
            ...prev,
            selectedShipmentDetail: value,
            selectedShipmentStatus: value === 'stale_over_3_days' ? ['in_transit'] : prev.selectedShipmentStatus
        }));
    }, []);

    // Get search text in comma-separated format for API calls
    const getSearchTextForAPI = useCallback((text) => {
        if (!text) return '';
        
        // Always return comma-separated format regardless of input format
        if (text.includes('\n')) {
            return text.split('\n').map(item => item.trim()).filter(item => item.length > 0).join(', ');
        }
        return text;
    }, []);

    // Handle search input change (only update local state)
    const handleSearchInputChange = useCallback((value) => {
        handleLocalFilterChange('searchText', value);
    }, [handleLocalFilterChange]);

    // Handle search execution
    const handleSearchExecute = useCallback((apiSearchText) => {
        onFilterChange(FILTER_TYPES.SEARCH, apiSearchText);
        onApplyFilters();
    }, [onFilterChange, onApplyFilters]);

    // Handle clear search with reload
    const handleClearSearch = useCallback(() => {
        // Clear the search text locally
        handleLocalFilterChange('searchText', '');
        // Clear the search filter and reload
        onFilterChange(FILTER_TYPES.SEARCH, '');
        onApplyFilters();
        // Trigger reload to refresh data
        onReload({ preserveSelection: false });
    }, [handleLocalFilterChange, onFilterChange, onApplyFilters, onReload]);

    const handleApplyFilters = useCallback(() => {
        // Apply all local filters using the generic handler with constants
        // Convert search text to API-compatible format (comma-separated)
        const apiSearchText = getSearchTextForAPI(localFilters.searchText);
        
        const filterUpdates = [
            { type: FILTER_TYPES.SEARCH, value: apiSearchText },
            { type: FILTER_TYPES.STATUS, value: localFilters.selectedStatus },
            { type: FILTER_TYPES.DATE_RANGE, value: localFilters.selectedDateRange },
            { type: FILTER_TYPES.SELLER_ACCOUNT, value: localFilters.selectedSellerAccount },
            { type: FILTER_TYPES.CUSTOMIZATION_STATUS, value: localFilters.selectedCustomizationStatus },
            { type: FILTER_TYPES.PRINTER, value: localFilters.selectedPrinter },
            { type: FILTER_TYPES.FULFILLMENT_STATUS, value: localFilters.selectedFulfillmentStatus },
            { type: FILTER_TYPES.FULFILLMENT_CHANNEL, value: localFilters.selectedFulfillmentChannel },
            { type: FILTER_TYPES.SHIPMENT_STATUS, value: localFilters.selectedShipmentStatus },
            { type: FILTER_TYPES.SHIPMENT_DETAIL, value: localFilters.selectedShipmentDetail },
            { type: FILTER_TYPES.SUPPORTER, value: localFilters.selectedSupporter },
            { type: FILTER_TYPES.CATEGORY, value: localFilters.selectedCategory },
            { type: FILTER_TYPES.MESSAGE_STATUS, value: localFilters.selectedMessageStatus },
            { type: FILTER_TYPES.SHIP_DATE_DUE, value: localFilters.selectedShipDateDue },
            { type: FILTER_TYPES.DELIVERY_DATE_DUE, value: localFilters.selectedDeliveryDateDue },
            { type: FILTER_TYPES.CONFIRM_SHIPMENT, value: localFilters.selectedConfirmShipment },
            { type: FILTER_TYPES.UPDATED_LAST_MILE, value: localFilters.selectedUpdatedLastMile },
            { type: FILTER_TYPES.MOCKUP_SENT_AT, value: localFilters.selectedMockupSentAt },
            { type: FILTER_TYPES.TITLE, value: localFilters.selectedTitle },
            { type: FILTER_TYPES.SELLER_SKU, value: localFilters.selectedSellerSku },
            { type: FILTER_TYPES.EMPLOYEE_ID, value: localFilters.selectedEmployeeId },
            { type: FILTER_TYPES.EXCLUDE_TITLE, value: localFilters.selectedExcludeTitle },
            { type: FILTER_TYPES.EXCLUDE_SELLER_SKU, value: localFilters.selectedExcludeSellerSku },
            { type: FILTER_TYPES.BASECOST, value: localFilters.selectedBasecost },
            { type: FILTER_TYPES.QUANTITY, value: localFilters.selectedQuantity }
        ];

        // Apply all filter updates
        filterUpdates.forEach(({ type, value }) => {
            onFilterChange(type, value);
        });

        // Execute the actual filter application
        onApplyFilters();
        // setIsFilterExpanded(false);
    }, [localFilters, onFilterChange, onApplyFilters, getSearchTextForAPI]);

    // Global keyboard shortcut: Shift+Enter to apply filters from anywhere on the page
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            // Check for Shift+Enter
            if (e.key === 'Enter' && e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
                handleApplyFilters();
            }
        };

        // Add event listener to document
        document.addEventListener('keydown', handleGlobalKeyDown);

        // Cleanup on unmount
        return () => {
            document.removeEventListener('keydown', handleGlobalKeyDown);
        };
    }, [handleApplyFilters]);

    // Individual clear functions for specific filters using constants
    const clearIndividualFilter = useCallback((filterKey) => {
        const defaults = FilterManager.getDefaultFilters();
        const clearValue = defaults[filterKey];
        handleLocalFilterChange(filterKey, clearValue);
    }, [handleLocalFilterChange]);

    // Handle individual filter tag removal and apply filters immediately
    const handleRemoveFilterTag = useCallback((filterKey) => {
        const defaults = FilterManager.getDefaultFilters();
        const filterType = FILTER_TYPE_MAP[filterKey];
        const clearValue = defaults[filterKey];

        setLocalFilters((prev) => ({ ...prev, [filterKey]: clearValue }));
        if (filterType) {
            onFilterChange(filterType, clearValue);
            onApplyFilters();
        }
    }, [FILTER_TYPE_MAP, onApplyFilters, onFilterChange]);

    // Check if local filters have changes compared to applied filters using utility
    const hasUnappliedChanges = useMemo(() => {
        return FilterManager.hasUnappliedChanges(localFilters, filters);
    }, [localFilters, filters]);

    // Memoize active filters count based on applied filters using utility
    const activeFiltersCount = useMemo(() => {
        return FilterManager.countActiveFilters(filters, { lockedEmployeeId });
    }, [filters, lockedEmployeeId]);

    // Memoize filter summary to prevent unnecessary re-renders
    const renderFilterSummary = useMemo(() => {
        return () => {
            const filterTags = [];

            console.log('Rendering filter summary with selectedShipDateDue:', selectedShipDateDue); // Debug log

            if (searchText) {
                filterTags.push(
                    <Tag 
                        key="search" 
                        color="blue" 
                        closable 
                        onClose={() => handleRemoveFilterTag('searchText')}
                    >
                        Search: {searchText}
                    </Tag>
                );
            }

            if (selectedStatus && Array.isArray(selectedStatus) && selectedStatus.length > 0) {
                const statusLabels = {
                    'Unshipped': 'Unshipped',
                    'PartiallyShipped': 'Partially Shipped',
                    'Shipped': 'Shipped',
                    'Canceled': 'Canceled'
                };
                const statusText = selectedStatus.map(status => statusLabels[status] || status).join(', ');
                filterTags.push(
                    <Tag 
                        key="status" 
                        color="green" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedStatus')}
                    >
                        Status: {statusText}
                    </Tag>
                );
            }

            if (selectedDateRange && selectedDateRange.length === 2) {
                filterTags.push(
                    <Tag 
                        key="dateRange" 
                        color="orange" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedDateRange')}
                    >
                        Date: {selectedDateRange[0].format('DD/MM/YYYY')} - {selectedDateRange[1].format('DD/MM/YYYY')}
                    </Tag>
                );
            }

            if (selectedSellerAccount && Array.isArray(selectedSellerAccount) && selectedSellerAccount.length > 0) {
                const accountText = selectedSellerAccount.map(accountId => {
                    const account = sellerAccounts?.find(acc => acc.id === accountId);
                    return account?.name || accountId;
                }).join(', ');
                filterTags.push(
                    <Tag 
                        key="account" 
                        color="purple" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedSellerAccount')}
                    >
                        Account: {accountText}
                    </Tag>
                );
            }

            if (selectedCustomizationStatus && selectedCustomizationStatus !== 'all') {
                const customizationLabels = {
                    'WaitingForSync': 'Waiting for sync',
                    'MockupSent': 'Mockup Sent',
                    'Rework': 'Rework',
                    'Confirming': 'Confirming',
                    'AutoFulfill': 'Auto Fulfill',
                    'CustomerConfirmed': 'Customer Confirmed',
                    'ConfirmedNoReply': 'Confirmed No Reply',
                    'TaskCreated': 'Task Created',
                    'Doing': 'Doing',
                    'InternalReview': 'Internal Review',
                    'MockupReady': 'Mockup Ready',
                    'NoCustomization': 'No Customization',
                    'HasCustomization': 'Has Customization'
                };
                filterTags.push(
                    <Tag 
                        key="customizationStatus" 
                        color="cyan" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedCustomizationStatus')}
                    >
                        Customization: {customizationLabels[selectedCustomizationStatus] || selectedCustomizationStatus}
                    </Tag>
                );
            }

            if (selectedPrinter && Array.isArray(selectedPrinter) && selectedPrinter.length > 0) {
                const printerText = selectedPrinter.map(printerId => {
                    const printer = printers?.find(p => p.id === printerId);
                    return printer?.name || printerId;
                }).join(', ');
                filterTags.push(
                    <Tag 
                        key="printer" 
                        color="magenta" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedPrinter')}
                    >
                        Printer: {printerText}
                    </Tag>
                );
            }

            if (selectedFulfillmentStatus && selectedFulfillmentStatus !== 'all') {
                filterTags.push(
                    <Tag 
                        key="fulfillmentStatus" 
                        color="volcano" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedFulfillmentStatus')}
                    >
                        Fulfillment: {selectedFulfillmentStatus}
                    </Tag>
                );
            }

            if (selectedFulfillmentChannel && selectedFulfillmentChannel !== 'all') {
                const channelLabels = {
                    'AFN': 'FBA (Fulfilled by Amazon)',
                    'MFN': 'FBM (Fulfilled by Merchant)'
                };
                filterTags.push(
                    <Tag 
                        key="fulfillmentChannel" 
                        color="orange" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedFulfillmentChannel')}
                    >
                        Channel: {channelLabels[selectedFulfillmentChannel] || selectedFulfillmentChannel}
                    </Tag>
                );
            }

            if (selectedShipmentStatus && Array.isArray(selectedShipmentStatus) && selectedShipmentStatus.length > 0) {
                const shipmentLabels = {
                    'info_received': 'Info. Received',
                    'in_transit': 'In Transit',
                    'out_for_delivery': 'Out for Delivery',
                    'failed_attempt': 'Failed Attempt',
                    'available_for_pickup': 'To Pick Up',
                    'delivered': 'Delivered',
                    'exception': 'Exception',
                    'pending': 'Pending'
                };
                const shipmentText = selectedShipmentStatus.map(status => shipmentLabels[status] || status).join(', ');
                filterTags.push(
                    <Tag 
                        key="shipmentStatus" 
                        color="geekblue" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedShipmentStatus')}
                    >
                        Shipment: {shipmentText}
                    </Tag>
                );
            }

            if (selectedShipmentDetail && selectedShipmentDetail !== 'all') {
                const shipmentDetailLabels = {
                    'stale_over_3_days': 'Quá 3 ngày không cập nhật'
                };
                filterTags.push(
                    <Tag 
                        key="shipmentDetail" 
                        color="red" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedShipmentDetail')}
                    >
                        Shipment Details: {shipmentDetailLabels[selectedShipmentDetail] || selectedShipmentDetail}
                    </Tag>
                );
            }

            if (selectedSupporter && selectedSupporter !== 'all') {
                const supporter = users?.find(u => u.id === selectedSupporter);
                filterTags.push(
                    <Tag 
                        key="supporter" 
                        color="lime" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedSupporter')}
                    >
                        Supporter: {supporter?.name || selectedSupporter}
                    </Tag>
                );
            }

            if (selectedCategory && Array.isArray(selectedCategory) && selectedCategory.length > 0) {
                const categoryText = selectedCategory.map(categoryId => {
                    const category = categories?.find(c => c.id === categoryId);
                    return category?.name || categoryId;
                }).join(', ');
                filterTags.push(
                    <Tag 
                        key="category" 
                        color="gold" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedCategory')}
                    >
                        Category: {categoryText}
                    </Tag>
                );
            }

            if (selectedMessageStatus && selectedMessageStatus !== 'all') {
                const messageLabels = {
                    'hasUnprocessedMessages': 'Has Unprocessed Messages',
                    'hasMessages': 'Has Messages',
                    'noMessages': 'No Messages'
                };
                filterTags.push(
                    <Tag 
                        key="messageStatus" 
                        color="red" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedMessageStatus')}
                    >
                        Messages: {messageLabels[selectedMessageStatus] || selectedMessageStatus}
                    </Tag>
                );
            }

            if (selectedShipDateDue && selectedShipDateDue !== 'all') {
                const shipDateLabels = {
                    'today': 'Ship Date Today',
                    'tomorrow': 'Ship Date Due (Within 1 Day)',
                    'dayAfterTomorrow': 'Ship Date Due (Within 2 Days)'
                };
                filterTags.push(
                    <Tag 
                        key="shipDateDue" 
                        color="orange" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedShipDateDue')}
                    >
                        Ship Date: {shipDateLabels[selectedShipDateDue] || selectedShipDateDue}
                    </Tag>
                );
            }

            if (selectedDeliveryDateDue && selectedDeliveryDateDue !== 'all') {
                const deliveryDateLabels = {
                    'today': 'Delivery Date Today',
                    'tomorrow': 'Delivery Date Due (Within 1 Day)',
                    'dayAfterTomorrow': 'Delivery Date Due (Within 2 Days)'
                };
                filterTags.push(
                    <Tag 
                        key="deliveryDateDue" 
                        color="purple" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedDeliveryDateDue')}
                    >
                        Delivery Date: {deliveryDateLabels[selectedDeliveryDateDue] || selectedDeliveryDateDue}
                    </Tag>
                );
            }

            if (selectedConfirmShipment && selectedConfirmShipment !== 'all') {
                const confirmShipmentLabels = {
                    'confirmed': 'Confirmed',
                    'pending': 'Pending',
                    'null': 'Not Set'
                };
                filterTags.push(
                    <Tag 
                        key="confirmShipment" 
                        color="green" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedConfirmShipment')}
                    >
                        Confirm Shipment: {confirmShipmentLabels[selectedConfirmShipment] || selectedConfirmShipment}
                    </Tag>
                );
            }

            if (selectedUpdatedLastMile && selectedUpdatedLastMile !== 'all') {
                const updatedLastMileLabels = {
                    'true': 'Has Updated Last Mile',
                    'false': 'No Updated Last Mile'
                };
                filterTags.push(
                    <Tag 
                        key="updatedLastMile" 
                        color="purple" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedUpdatedLastMile')}
                    >
                        Updated Last Mile: {updatedLastMileLabels[selectedUpdatedLastMile] || selectedUpdatedLastMile}
                    </Tag>
                );
            }

            if (selectedMockupSentAt && selectedMockupSentAt !== 'all') {
                const mockupSentAtLabels = {
                    'before12h': 'Mockup Sent Before 12 Hours',
                    'after12h': 'Mockup Sent After 12 Hours'
                };
                filterTags.push(
                    <Tag 
                        key="mockupSentAt" 
                        color="purple" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedMockupSentAt')}
                    >
                        Mockup Sent: {mockupSentAtLabels[selectedMockupSentAt] || selectedMockupSentAt}
                    </Tag>
                );
            }

            if (selectedTitle && selectedTitle.trim()) {
                filterTags.push(
                    <Tag 
                        key="title" 
                        color="magenta" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedTitle')}
                    >
                        Title: {selectedTitle}
                    </Tag>
                );
            }

            if (selectedSellerSku && selectedSellerSku.trim()) {
                filterTags.push(
                    <Tag 
                        key="sellerSku" 
                        color="volcano" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedSellerSku')}
                    >
                        Seller SKU: {selectedSellerSku}
                    </Tag>
                );
            }

            if (selectedEmployeeId && selectedEmployeeId.trim()) {
                filterTags.push(
                    <Tag 
                        key="employeeId" 
                        color="gold" 
                        closable={!lockedEmployeeId}
                        onClose={!lockedEmployeeId ? () => handleRemoveFilterTag('selectedEmployeeId') : undefined}
                    >
                        {lockedEmployeeId && <LockOutlined style={{ marginRight: 4 }} />}
                        Employee ID: {selectedEmployeeId}
                    </Tag>
                );
            }

            if (selectedExcludeTitle && selectedExcludeTitle.trim()) {
                filterTags.push(
                    <Tag 
                        key="excludeTitle" 
                        color="red" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedExcludeTitle')}
                    >
                        Exclude Title: {selectedExcludeTitle}
                    </Tag>
                );
            }

            if (selectedExcludeSellerSku && selectedExcludeSellerSku.trim()) {
                filterTags.push(
                    <Tag 
                        key="excludeSellerSku" 
                        color="red" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedExcludeSellerSku')}
                    >
                        Exclude Seller SKU: {selectedExcludeSellerSku}
                    </Tag>
                );
            }

            if (selectedBasecost && selectedBasecost !== 'all') {
                const basecostLabels = {
                    'null': 'Base Cost: Not Set (null)',
                    'zero': 'Base Cost: Zero ($0)',
                    'null_or_zero': 'Base Cost: Null or Zero',
                    'has_value': 'Base Cost: Has Value (> $0)'
                };
                filterTags.push(
                    <Tag 
                        key="basecost" 
                        color="cyan" 
                        closable 
                        onClose={() => handleRemoveFilterTag('selectedBasecost')}
                    >
                        {basecostLabels[selectedBasecost] || selectedBasecost}
                    </Tag>
                );
            }

            if (selectedQuantity !== null && selectedQuantity !== undefined && selectedQuantity !== '') {
                filterTags.push(
                    <Tag
                        key="quantity"
                        color="blue"
                        closable
                        onClose={() => handleRemoveFilterTag('selectedQuantity')}
                    >
                        Qty: {selectedQuantity}
                    </Tag>
                );
            }

            return filterTags;
        };
    }, [searchText, selectedStatus, selectedDateRange, selectedSellerAccount, selectedCustomizationStatus, selectedPrinter, selectedFulfillmentStatus, selectedFulfillmentChannel, selectedShipmentStatus, selectedShipmentDetail, selectedSupporter, selectedCategory, selectedMessageStatus, selectedShipDateDue, selectedDeliveryDateDue, selectedConfirmShipment, selectedUpdatedLastMile, selectedMockupSentAt, selectedTitle, selectedSellerSku, selectedEmployeeId, selectedExcludeTitle, selectedExcludeSellerSku, selectedBasecost, selectedQuantity, sellerAccounts, printers, users, categories, handleRemoveFilterTag]);

    return (
        <div style={{ paddingBottom: 10, borderBottom: '1px solid #f0f0f0' }}>
            {/* Basic Search Row */}
            <Row gutter={16} align="middle">
                <Col flex="auto">
                    <Row>
                        <Col span={6}>
                            <SearchInput
                                value={localFilters.searchText}
                                onChange={handleSearchInputChange}
                                onSearch={handleSearchExecute}
                                onClear={handleClearSearch}
                                onApplyFilters={handleApplyFilters}
                                placeholder="Search by order ID, product name or SKU (use commas for multiple IDs/SKUs)"
                                textareaPlaceholder="Search by order ID, product name or SKU (one per line or use commas). Shift+Enter to apply all filters"
                            />
                            {/* Show search hint for multiple values */}
                            {localFilters.searchText && (localFilters.searchText.includes(',') || localFilters.searchText.includes('\n')) && (
                                <div style={{ 
                                    fontSize: '12px', 
                                    color: '#1890ff', 
                                    marginTop: '4px',
                                    fontStyle: 'italic'
                                }}>
                                    🔍 Searching for {getSearchTextForAPI(localFilters.searchText).split(',').filter(val => val.trim().length > 0).length} items (Order IDs/SKUs only)
                                </div>
                            )}
                            {/* Show hint when user has unsaved changes */}
                            {hasUnappliedChanges && (
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize: 11,
                                        fontStyle: 'italic',
                                        color: '#fa8c16'
                                    }}
                                >
                                    You have unsaved filter changes. Click "Apply Filters" to search.
                                </Text>
                            )}
                        </Col>
                    </Row>
                </Col>
                <Col>
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => onReload({ preserveSelection: true })}
                            loading={loading}
                            title="Reload with current filters"
                        >
                            Reload
                        </Button>
                        <Button
                            type={isFilterExpanded ? "primary" : "default"}
                            icon={<FilterOutlined />}
                            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                        >
                            Filters
                            {activeFiltersCount > 0 && (
                                <span style={{
                                    marginLeft: 4,
                                    backgroundColor: '#ff4d4f',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: 18,
                                    height: 18,
                                    display: 'inline-block',
                                    textAlign: 'center',
                                    fontSize: 12,
                                    lineHeight: '18px'
                                }}>
                                    {activeFiltersCount}
                                </span>
                            )}
                            {hasUnappliedChanges && (
                                <span style={{
                                    marginLeft: 4,
                                    backgroundColor: '#fa8c16',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: 6,
                                    height: 6,
                                    display: 'inline-block'
                                }} title="You have unsaved changes">
                                </span>
                            )}
                            {isFilterExpanded ? <UpOutlined style={{ marginLeft: 4 }} /> : <DownOutlined style={{ marginLeft: 4 }} />}
                        </Button>
                        {activeFiltersCount > 0 && (
                            <Button
                                icon={<ClearOutlined />}
                                onClick={handleClearFilters}
                                title="Clear all filters"
                            >
                                Clear Filters
                            </Button>
                        )}
                    </Space>
                </Col>
            </Row>

            {/* Active Filters Summary */}
            {activeFiltersCount > 0 && !isFilterExpanded && (
                <Row style={{ marginTop: 12 }}>
                    <Col span={24}>
                        <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
                            Active filters:
                        </Text>
                        <Space size={4} wrap>
                            {renderFilterSummary()}
                        </Space>
                    </Col>
                </Row>
            )}

            {/* Advanced Filters */}
            {isFilterExpanded && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #f0f0f0' }}>
                    <Row gutter={[16, 16]}>
                        {/* Order Status Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: (localFilters.selectedStatus && Array.isArray(localFilters.selectedStatus) && localFilters.selectedStatus.length > 0) ? '#1890ff' : undefined }}>
                                    <ShopOutlined style={{ marginRight: 4 }} />
                                    Order Status
                                </Text>
                                {(localFilters.selectedStatus && Array.isArray(localFilters.selectedStatus) && localFilters.selectedStatus.length > 0) && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedStatus')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear status filter"
                                    />
                                )}
                            </div>
                            <Select
                                mode="multiple"
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedStatus', value)}
                                value={localFilters.selectedStatus === 'all' ? [] : (Array.isArray(localFilters.selectedStatus) ? localFilters.selectedStatus : [localFilters.selectedStatus])}
                                placeholder="Select status (multiple allowed)"
                                allowClear
                                maxTagCount="responsive"
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="Pending">Pending</Option>
                                <Option value="Unshipped">Unshipped</Option>
                                <Option value="PartiallyShipped">Partially Shipped</Option>
                                <Option value="Shipped">Shipped</Option>
                                <Option value="Canceled">Canceled</Option>
                            </Select>
                        </Col>

                        {/* Customization Status Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedCustomizationStatus !== 'all' ? '#1890ff' : undefined }}>
                                    <BgColorsOutlined style={{ marginRight: 4 }} />
                                    Customization Status
                                </Text>
                                {localFilters.selectedCustomizationStatus !== 'all' && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedCustomizationStatus')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear customization status filter"
                                    />
                                )}
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedCustomizationStatus', value)}
                                value={localFilters.selectedCustomizationStatus}
                                placeholder="Select customization status"
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="all">All Status</Option>
                                <Option value="WaitingForSync">Waiting for sync</Option>
                                <Option value="NoCustomization">No Customization</Option>
                                <Option value="HasCustomization">Has Customization</Option>

                                <OptGroup label="🎨 Design Process">
                                    <Option value="TaskCreated">Task Created</Option>
                                    <Option value="Doing">Doing</Option>
                                    <Option value="InternalReview">Internal Review</Option>
                                    <Option value="Rework">Rework</Option>
                                    <Option value="MockupReady">Mockup Ready</Option>
                                </OptGroup>

                                <OptGroup label="✅ Customer Confirmation">
                                    <Option value="MockupSent">Mockup Sent</Option>
                                    <Option value="Confirming">Confirming</Option>
                                    <Option value="CustomerConfirmed">Customer Confirmed</Option>
                                    <Option value="ConfirmedNoReply">Confirmed No Reply</Option>
                                </OptGroup>

                                <OptGroup label="🚀 Production">
                                    <Option value="AutoFulfill">Auto Fulfill</Option>
                                </OptGroup>
                            </Select>
                        </Col>

                        {/* Purchase Date Range Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedDateRange && localFilters.selectedDateRange.length === 2 ? '#1890ff' : undefined }}>
                                    <CalendarOutlined style={{ marginRight: 4 }} />
                                    Purchase Date
                                </Text>
                                {localFilters.selectedDateRange && localFilters.selectedDateRange.length === 2 && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedDateRange')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear date range filter"
                                    />
                                )}
                            </div>
                            <RangePicker
                                style={{ width: '100%' }}
                                onChange={handleDateRangeChange}
                                value={localFilters.selectedDateRange || null}
                                format="DD/MM/YYYY"
                                placeholder={['From date', 'To date']}
                                allowClear={true}
                                ranges={{
                                    'Today': [dayjs(), dayjs()],
                                    'Last 7 days': [dayjs().subtract(7, 'days'), dayjs()],
                                    'Last 30 days': [dayjs().subtract(30, 'days'), dayjs()],
                                    'Last 90 days': [dayjs().subtract(90, 'days'), dayjs()],
                                    'This month': [dayjs().startOf('month'), dayjs().endOf('month')],
                                    'Last month': [
                                        dayjs().subtract(1, 'month').startOf('month'),
                                        dayjs().subtract(1, 'month').endOf('month')
                                    ]
                                }}
                            />
                        </Col>

                        {/* Seller Account Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: (localFilters.selectedSellerAccount && Array.isArray(localFilters.selectedSellerAccount) && localFilters.selectedSellerAccount.length > 0) ? '#1890ff' : undefined }}>
                                    <ShopOutlined style={{ marginRight: 4 }} />
                                    Seller Account
                                </Text>
                                {(localFilters.selectedSellerAccount && Array.isArray(localFilters.selectedSellerAccount) && localFilters.selectedSellerAccount.length > 0) && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedSellerAccount')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear seller account filter"
                                    />
                                )}
                            </div>
                            <Select
                                mode="multiple"
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedSellerAccount', value)}
                                value={localFilters.selectedSellerAccount === 'all' ? [] : (Array.isArray(localFilters.selectedSellerAccount) ? localFilters.selectedSellerAccount : [localFilters.selectedSellerAccount])}
                                placeholder="Select accounts (multiple allowed)"
                                allowClear
                                maxTagCount="responsive"
                                loading={!sellerAccounts}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {sellerAccounts && sellerAccounts.map(account => (
                                    <Option key={account.id} value={account.id}>
                                        {account.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>

                        {/* Printer Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: (localFilters.selectedPrinter && Array.isArray(localFilters.selectedPrinter) && localFilters.selectedPrinter.length > 0) ? '#1890ff' : undefined }}>
                                    <PrinterOutlined style={{ marginRight: 4 }} />
                                    Printer
                                </Text>
                                {(localFilters.selectedPrinter && Array.isArray(localFilters.selectedPrinter) && localFilters.selectedPrinter.length > 0) && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedPrinter')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear printer filter"
                                    />
                                )}
                            </div>
                            <Select
                                mode="multiple"
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedPrinter', value)}
                                value={localFilters.selectedPrinter === 'all' ? [] : (Array.isArray(localFilters.selectedPrinter) ? localFilters.selectedPrinter : [localFilters.selectedPrinter])}
                                placeholder="Select printers (multiple allowed)"
                                allowClear
                                maxTagCount="responsive"
                                loading={!printers}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {printers && printers
                                    // Hide inactive printers, but keep ones already selected so their tag still shows a name
                                    .filter(printer => printer.isActive || [].concat(localFilters.selectedPrinter).includes(printer.id))
                                    .map(printer => (
                                    <Option key={printer.id} value={printer.id}>
                                        {printer.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        {/* Fulfillment Status Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedFulfillmentStatus !== 'all' ? '#1890ff' : undefined }}>
                                    <ProductOutlined style={{ marginRight: 4 }} />
                                    Fulfillment Status
                                </Text>
                                {localFilters.selectedFulfillmentStatus !== 'all' && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedFulfillmentStatus')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear fulfillment status filter"
                                    />
                                )}
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedFulfillmentStatus', value)}
                                value={localFilters.selectedFulfillmentStatus}
                                placeholder="Select fulfillment status"
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="all">All Status</Option>
                                <Option value="Unfulfilled">Unfulfilled</Option>
                                <Option value="Submitted">Submitted</Option>
                                <Option value="Fulfilled">Fulfilled</Option>
                                <Option value="Shipped">Shipped</Option>
                                <Option value="Canceled">Canceled</Option>
                                <Option value="Failed">Failed</Option>
                            </Select>
                        </Col>

                        {/* Fulfillment Channel Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedFulfillmentChannel !== 'all' ? '#1890ff' : undefined }}>
                                    <ShopOutlined style={{ marginRight: 4 }} />
                                    Fulfillment Channel
                                </Text>
                                {localFilters.selectedFulfillmentChannel !== 'all' && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedFulfillmentChannel')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear fulfillment channel filter"
                                    />
                                )}
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedFulfillmentChannel', value)}
                                value={localFilters.selectedFulfillmentChannel}
                                placeholder="Select fulfillment channel"
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="all">All Channels</Option>
                                <Option value="AFN">FBA (Fulfilled by Amazon)</Option>
                                <Option value="MFN">FBM (Fulfilled by Merchant)</Option>
                            </Select>
                        </Col>

                        {/* Shipment Status Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: (localFilters.selectedShipmentStatus && Array.isArray(localFilters.selectedShipmentStatus) && localFilters.selectedShipmentStatus.length > 0) ? '#1890ff' : undefined }}>
                                    <TruckOutlined style={{ marginRight: 4 }} />
                                    Shipment Status
                                </Text>
                                {(localFilters.selectedShipmentStatus && Array.isArray(localFilters.selectedShipmentStatus) && localFilters.selectedShipmentStatus.length > 0) && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedShipmentStatus')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear shipment status filter"
                                    />
                                )}
                            </div>
                            <Select
                                mode="multiple"
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedShipmentStatus', value)}
                                value={localFilters.selectedShipmentStatus === 'all' ? [] : (Array.isArray(localFilters.selectedShipmentStatus) ? localFilters.selectedShipmentStatus : [localFilters.selectedShipmentStatus])}
                                placeholder="Select shipment status (multiple allowed)"
                                allowClear
                                maxTagCount="responsive"
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="info_received">Info. Received</Option>
                                <Option value="in_transit">In Transit</Option>
                                <Option value="out_for_delivery">Out for Delivery</Option>
                                <Option value="failed_attempt">Failed Attempt</Option>
                                <Option value="available_for_pickup">To Pick Up</Option>
                                <Option value="delivered">Delivered</Option>
                                <Option value="exception">Exception</Option>
                                <Option value="pending">Pending</Option>
                            </Select>
                        </Col>

                        {/* Shipment Details Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedShipmentDetail !== 'all' ? '#1890ff' : undefined }}>
                                    <TruckOutlined style={{ marginRight: 4 }} />
                                    Shipment Details
                                </Text>
                                {localFilters.selectedShipmentDetail !== 'all' && (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CloseCircleOutlined />}
                                        onClick={() => clearIndividualFilter('selectedShipmentDetail')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear shipment details filter"
                                    />
                                )}
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                onChange={handleShipmentDetailChange}
                                value={localFilters.selectedShipmentDetail}
                                placeholder="Select shipment details"
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="all">All Details</Option>
                                <Option value="stale_over_3_days">Quá 3 ngày không cập nhật</Option>
                            </Select>
                        </Col>

                        {/* Supporter Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedSupporter !== 'all' ? '#1890ff' : undefined }}>
                                    <UserOutlined style={{ marginRight: 4 }} />
                                    Supporter
                                </Text>
                                {localFilters.selectedSupporter !== 'all' && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedSupporter')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear supporter filter"
                                    />
                                )}
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedSupporter', value)}
                                value={localFilters.selectedSupporter}
                                placeholder="Select supporter"
                                loading={!users}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="all">All Supporters</Option>
                                {users && users.filter(user=> user.role=='Support' || user.role=='Manager').map(user => (
                                    <Option key={user.id} value={user.id}>
                                        {user.username}
                                    </Option>
                                ))}
                            </Select>
                        </Col>

                        {/* Category Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: (localFilters.selectedCategory && Array.isArray(localFilters.selectedCategory) && localFilters.selectedCategory.length > 0) ? '#1890ff' : undefined }}>
                                    <TagOutlined style={{ marginRight: 4 }} />
                                    Category
                                </Text>
                                {(localFilters.selectedCategory && Array.isArray(localFilters.selectedCategory) && localFilters.selectedCategory.length > 0) && (
                                    <Button
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedCategory')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear category filter"
                                    />
                                )}
                            </div>
                            <Select
                                mode="multiple"
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedCategory', value)}
                                value={localFilters.selectedCategory === 'all' ? [] : (Array.isArray(localFilters.selectedCategory) ? localFilters.selectedCategory : [localFilters.selectedCategory])}
                                placeholder="Select categories (multiple allowed)"
                                allowClear
                                maxTagCount="responsive"
                                loading={!categories}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {categories && categories.map(category => (
                                    <Option key={category.id} value={category.id}>
                                        {category.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>

                        {/* Message Status Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedMessageStatus !== 'all' ? '#1890ff' : undefined }}>
                                    <MessageOutlined style={{ marginRight: 4 }} />
                                    Message Status
                                </Text>
                                {localFilters.selectedMessageStatus !== 'all' && (
                                    <Button
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedMessageStatus')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear message status filter"
                                    />
                                )}
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedMessageStatus', value)}
                                value={localFilters.selectedMessageStatus}
                                placeholder="Select message status"
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="all">All Orders</Option>
                                <Option value="hasUnprocessedMessages">Has Unprocessed Messages</Option>
                                <Option value="hasMessages">Has Messages</Option>
                                <Option value="noMessages">No Messages</Option>
                            </Select>
                        </Col>

                        {/* Ship Date Due Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedShipDateDue !== 'all' ? '#1890ff' : undefined }}>
                                    <CalendarOutlined style={{ marginRight: 4, color: '#ff4d4f' }} />
                                    Ship Date Due
                                </Text>
                                {localFilters.selectedShipDateDue !== 'all' && (
                                    <Button
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedShipDateDue')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear ship date due filter"
                                    />
                                )}
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedShipDateDue', value)}
                                value={localFilters.selectedShipDateDue}
                                placeholder="Select ship date status"
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="all">All Orders</Option>
                                <Option value="today">Ship Date Today</Option>
                                <Option value="tomorrow">Ship Date Due (Within 1 Day)</Option>
                                <Option value="dayAfterTomorrow">Ship Date Due (Within 2 Days)</Option>
                            </Select>
                        </Col>

                        {/* Delivery Date Due Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedDeliveryDateDue !== 'all' ? '#1890ff' : undefined }}>
                                    <CalendarOutlined style={{ marginRight: 4, color: '#722ed1' }} />
                                    Delivery Date Due
                                </Text>
                                {localFilters.selectedDeliveryDateDue !== 'all' && (
                                    <Button
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedDeliveryDateDue')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear delivery date due filter"
                                    />
                                )}
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedDeliveryDateDue', value)}
                                value={localFilters.selectedDeliveryDateDue}
                                placeholder="Select delivery date status"
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="all">All Orders</Option>
                                <Option value="today">Delivery Overdue (Up to Today)</Option>
                                <Option value="tomorrow">Delivery Date Due (Within 1 Day)</Option>
                                <Option value="dayAfterTomorrow">Delivery Date Due (Within 2 Days)</Option>
                            </Select>
                        </Col>

                        {/* Confirm Shipment Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedConfirmShipment !== 'all' ? '#1890ff' : undefined }}>
                                    <CheckOutlined style={{ marginRight: 4, color: '#52c41a' }} />
                                    Confirm Shipment
                                </Text>
                                {localFilters.selectedConfirmShipment !== 'all' && (
                                    <Button
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedConfirmShipment')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear confirm shipment filter"
                                    />
                                )}
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedConfirmShipment', value)}
                                value={localFilters.selectedConfirmShipment}
                                placeholder="Select confirmation status"
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="all">All Orders</Option>
                                <Option value="confirmed">Confirmed</Option>
                                <Option value="pending">Pending</Option>
                                <Option value="null">Not Set</Option>
                            </Select>
                        </Col>

                        {/* Updated Last Mile Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedUpdatedLastMile !== 'all' ? '#1890ff' : undefined }}>
                                    <TruckOutlined style={{ marginRight: 4, color: '#722ed1' }} />
                                    Updated Last Mile
                                </Text>
                                {localFilters.selectedUpdatedLastMile !== 'all' && (
                                    <Button
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedUpdatedLastMile')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear updated last mile filter"
                                    />
                                )}
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedUpdatedLastMile', value)}
                                value={localFilters.selectedUpdatedLastMile}
                                placeholder="Select updated last mile status"
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="all">All Orders</Option>
                                <Option value="true">Has Updated Last Mile</Option>
                                <Option value="false">No Updated Last Mile</Option>
                            </Select>
                        </Col>

                        {/* Mockup Sent At Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedMockupSentAt !== 'all' ? '#1890ff' : undefined }}>
                                    <CalendarOutlined style={{ marginRight: 4, color: '#722ed1' }} />
                                    Mockup Sent At
                                </Text>
                                {localFilters.selectedMockupSentAt !== 'all' && (
                                    <Button
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedMockupSentAt')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear mockup sent at filter"
                                    />
                                )}
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedMockupSentAt', value)}
                                value={localFilters.selectedMockupSentAt}
                                placeholder="Select mockup sent status"
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                <Option value="all">All Orders</Option>
                                <Option value="before12h">Sent Before 12 Hours</Option>
                                <Option value="after12h">Sent After 12 Hours</Option>
                            </Select>
                        </Col>

                        {/* Quantity Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedQuantity !== null && localFilters.selectedQuantity !== undefined ? '#1890ff' : undefined }}>
                                    <NumberOutlined style={{ marginRight: 4, color: '#1677ff' }} />
                                    Qty
                                </Text>
                                {localFilters.selectedQuantity !== null && localFilters.selectedQuantity !== undefined && (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CloseCircleOutlined />}
                                        onClick={() => clearIndividualFilter('selectedQuantity')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear quantity filter"
                                    />
                                )}
                            </div>
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                precision={0}
                                step={1}
                                value={localFilters.selectedQuantity}
                                onChange={(value) => handleLocalFilterChange('selectedQuantity', value)}
                                onPressEnter={handleApplyFilters}
                                placeholder="Filter exact quantity"
                            />
                        </Col>
                    </Row>

                    {/* Second Row: New Text Filters */}
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        {/* Title Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedTitle && localFilters.selectedTitle.trim() ? '#1890ff' : undefined }}>
                                    <TagOutlined style={{ marginRight: 4 }} />
                                    Product Title
                                </Text>
                                {localFilters.selectedTitle && localFilters.selectedTitle.trim() && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedTitle')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear title filter"
                                    />
                                )}
                            </div>
                            <Input
                                style={{ width: '100%' }}
                                onChange={(e) => handleLocalFilterChange('selectedTitle', e.target.value)}
                                value={localFilters.selectedTitle}
                                placeholder="Search by product title"
                                allowClear
                            />
                        </Col>

                        {/* Seller SKU Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedSellerSku && localFilters.selectedSellerSku.trim() ? '#1890ff' : undefined }}>
                                    <MenuOutlined style={{ marginRight: 4 }} />
                                    Seller SKU
                                </Text>
                                {localFilters.selectedSellerSku && localFilters.selectedSellerSku.trim() && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedSellerSku')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear seller SKU filter"
                                    />
                                )}
                            </div>
                            <Input
                                style={{ width: '100%' }}
                                onChange={(e) => handleLocalFilterChange('selectedSellerSku', e.target.value)}
                                value={localFilters.selectedSellerSku}
                                placeholder="Search by seller SKU"
                                allowClear
                            />
                        </Col>

                        {/* Employee ID Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedEmployeeId && localFilters.selectedEmployeeId.trim() ? (lockedEmployeeId ? '#faad14' : '#1890ff') : undefined }}>
                                    {lockedEmployeeId
                                        ? <LockOutlined style={{ marginRight: 4 }} />
                                        : <UserOutlined style={{ marginRight: 4 }} />
                                    }
                                    Employee ID
                                </Text>
                                {!lockedEmployeeId && localFilters.selectedEmployeeId && localFilters.selectedEmployeeId.trim() && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedEmployeeId')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear employee ID filter"
                                    />
                                )}
                            </div>
                            <Tooltip
                                title={lockedEmployeeId ? `Filter tự động theo mã nhân viên của bạn (${lockedEmployeeId})` : ''}
                                placement="top"
                            >
                                <Input
                                    style={{ width: '100%' }}
                                    onChange={(e) => !lockedEmployeeId && handleLocalFilterChange('selectedEmployeeId', e.target.value)}
                                    value={localFilters.selectedEmployeeId}
                                    placeholder={lockedEmployeeId ? `Đang lọc: ${lockedEmployeeId}` : 'Search by employee ID (6 chars from SKU)'}
                                    allowClear={!lockedEmployeeId}
                                    disabled={!!lockedEmployeeId}
                                    suffix={lockedEmployeeId ? <LockOutlined style={{ color: '#faad14' }} /> : null}
                                />
                            </Tooltip>
                        </Col>
                    </Row>

                    {/* Third Row: Exclude Filters */}
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        {/* Exclude Title Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedExcludeTitle && localFilters.selectedExcludeTitle.trim() ? '#ff4d4f' : undefined }}>
                                    <CloseCircleOutlined style={{ marginRight: 4 }} />
                                    Exclude Title
                                </Text>
                                {localFilters.selectedExcludeTitle && localFilters.selectedExcludeTitle.trim() && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedExcludeTitle')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear exclude title filter"
                                    />
                                )}
                            </div>
                            <Input
                                style={{ width: '100%' }}
                                onChange={(e) => handleLocalFilterChange('selectedExcludeTitle', e.target.value)}
                                value={localFilters.selectedExcludeTitle}
                                placeholder="Exclude products by title"
                                allowClear
                            />
                        </Col>

                        {/* Exclude Seller SKU Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedExcludeSellerSku && localFilters.selectedExcludeSellerSku.trim() ? '#ff4d4f' : undefined }}>
                                    <CloseCircleOutlined style={{ marginRight: 4 }} />
                                    Exclude Seller SKU
                                </Text>
                                {localFilters.selectedExcludeSellerSku && localFilters.selectedExcludeSellerSku.trim() && (
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => clearIndividualFilter('selectedExcludeSellerSku')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear exclude seller SKU filter"
                                    />
                                )}
                            </div>
                            <Input
                                style={{ width: '100%' }}
                                onChange={(e) => handleLocalFilterChange('selectedExcludeSellerSku', e.target.value)}
                                value={localFilters.selectedExcludeSellerSku}
                                placeholder="Exclude products by seller SKU"
                                allowClear
                            />
                        </Col>

                        {/* Empty cols to maintain alignment */}
                        <Col xs={24} sm={12} md={4}></Col>
                    </Row>

                    {/* Fourth Row: Base Cost Filter */}
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        {/* Base Cost Filter */}
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <Text strong style={{ color: localFilters.selectedBasecost !== 'all' ? '#1890ff' : undefined }}>
                                    <LineOutlined style={{ marginRight: 4, color: '#13c2c2' }} />
                                    Base Cost
                                </Text>
                                {localFilters.selectedBasecost !== 'all' && (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CloseCircleOutlined />}
                                        onClick={() => clearIndividualFilter('selectedBasecost')}
                                        style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                                        title="Clear base cost filter"
                                    />
                                )}
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                onChange={(value) => handleLocalFilterChange('selectedBasecost', value)}
                                value={localFilters.selectedBasecost}
                                placeholder="Filter by base cost"
                            >
                                <Option value="all">All Orders</Option>
                                <Option value="null">Not Set (null)</Option>
                                <Option value="zero">Zero ($0)</Option>
                                <Option value="null_or_zero">Null or Zero</Option>
                                <Option value="has_value">Has Value (&gt; $0)</Option>
                            </Select>
                        </Col>
                    </Row>

                    {/* Filter Actions */}
                    <Row justify="end" style={{ marginTop: 16 }}>
                        <Col>
                            <Space>
                                <Button onClick={handleClearFilters}>
                                    Clear All
                                </Button>
                                <Button
                                    type={hasUnappliedChanges ? "primary" : "default"}
                                    onClick={handleApplyFilters}
                                    icon={hasUnappliedChanges ? <CheckOutlined /> : <FilterOutlined />}
                                >
                                    {hasUnappliedChanges ? 'Apply Changes' : 'Apply Filters'}
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </div>
            )}
        </div>
    );
};

export default OrdersFilter;
